import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import { getAccountDetailsAppAccountsAccountsAccountIdGetOptions } from '@/client/@tanstack/react-query.gen'
import { ScrollArea } from '@/components/ui/scroll-area'
import { TabsContent } from '@/components/ui/tabs'
import { CAPABILITY_LABELS, IDENTITY_LABELS } from '@/lib/company-labels'
import { AccountOpportunities } from './-components/account-opportunities'
import { BuildingFootprintCard } from './-components/building-footprint'
import { ExpandedCardProvider } from './-components/expanded-card-provider'
import { CompanyDescription } from './-components/company-description'
import { CompanyFinancials } from './-components/company-financials'
import { CompanySpecialties } from './-components/company-specialties'
import { CompetitorsWidget } from './-components/competitors-widget'
import { EquipmentWidget } from './-components/equipment-widget'
import { IndustriesWidget } from './-components/industries-widget'
import { ProductionReports } from './-components/production-reports'
import { ProductsWidget } from './-components/products-widget'
import { SuppliersWidget } from './-components/suppliers-widget'

export const Route = createFileRoute(
  '/_authed/_user/accounts/$accountId/overview/',
)({
  component: AccountOverviewTab,
})

function AccountOverviewTab() {
  const { accountId } = Route.useParams()
  const { data: accountDetails } = useQuery(
    getAccountDetailsAppAccountsAccountsAccountIdGetOptions({
      path: { account_id: accountId },
    }),
  )

  const { capabilityLabels, identityLabels } = useMemo(() => {
    const allLabels = accountDetails?.misc?.labels ?? []
    const capabilities: string[] = []
    const identities: string[] = []
    for (const label of allLabels) {
      if (CAPABILITY_LABELS.has(label)) {
        capabilities.push(label)
      }
      else if (IDENTITY_LABELS.has(label)) {
        identities.push(label)
      }
      else {
        console.error(`[CompanyLabels] Unknown label type: "${label}" — add to IDENTITY_LABELS or CAPABILITY_LABELS`)
      }
    }
    return { capabilityLabels: capabilities, identityLabels: identities }
  }, [accountDetails?.misc?.labels])

  return (
    <TabsContent value="overview" className="mt-0 flex-1">
      <ScrollArea className="h-full w-full">
        <div
          className="min-h-screen py-3 px-4 lg:px-6"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(203,213,225,0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(203,213,225,0.3) 1px, transparent 1px)',
            backgroundSize: '16px 16px',
          }}
        >
          <ExpandedCardProvider>
            {/* items-start: grid items default to stretch, which inflates every
                card in a row to the tallest one — visible when expanding a card */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 items-start">
              <AccountOpportunities
                accountId={accountId}
                accountDetails={accountDetails}
              />
              {!!(accountDetails?.firmographics?.companyDescription || identityLabels.length > 0) && (
                <CompanyDescription
                  description={accountDetails?.firmographics?.companyDescription ?? undefined}
                  identityLabels={identityLabels}
                  naicsCodeDescription={accountDetails?.firmographics?.naicsCodeDescription ?? undefined}
                  businessCategory={accountDetails?.misc?.businessCategory ?? undefined}
                />
              )}
              <BuildingFootprintCard accountId={accountId} />
              <CompanyFinancials
                accountDetails={accountDetails}
              />
              <ProductionReports
                report={accountDetails?.productionReport ?? undefined}
              />
              {!!accountDetails?.misc?.industries
                && accountDetails?.misc?.industries.length > 0 && (
                <IndustriesWidget
                  industries={
                    accountDetails?.misc?.industries || []
                  }
                  verticals={
                    accountDetails?.misc?.verticals || []
                  }
                />
              )}
              <SuppliersWidget accountId={accountId} accountName={accountDetails?.account?.accountName ?? 'Account'} />
              <CompetitorsWidget
                competitors={accountDetails?.competitors?.competitors || []}
              />
              {!!accountDetails?.misc?.specialties
                && accountDetails?.misc?.specialties.length > 0 && (
                <CompanySpecialties
                  specialties={
                    accountDetails?.misc?.specialties || []
                  }
                />
              )}
              <ProductsWidget
                products={accountDetails?.projects?.offerings || []}
              />
              {!!accountDetails?.misc?.equipments
                && accountDetails?.misc?.equipments.length > 0 && (
                <EquipmentWidget accountId={accountId} capabilityLabels={capabilityLabels} />
              )}
            </div>
          </ExpandedCardProvider>
        </div>
      </ScrollArea>
    </TabsContent>
  )
}
